<template>
  <!-- Manage Deployment Dialog -->
  <SmDialog
    v-model:visibility="visibility"
    :title="i18n.t('browserExtension.manageDeployments')"
    width="large">
    <!-- Header -->
    <template #close>
      <div style="display: flex; gap: 1rem; align-items: center">
        <SmButton @click="createDeployment()">
          {{ i18n.t('browserExtension.createDeployment') }}
        </SmButton>
        <SmButtonClose @close="visibility = false" />
      </div>
    </template>

    <SmTable :data="deployments" :columns="columns" key-field="_id">
      <template #name="{ row }">
        <div>
          <TextMultiline>{{ row.name }}</TextMultiline>
          <TextMultiline v-if="row.description" class="text-contrast-muted">
            {{ row.description }}
          </TextMultiline>
        </div>
      </template>

      <template #token="{ row }">
        <div class="group flex h-full w-full items-center gap-4">
          <p v-if="token[row._id]" class="flex items-center gap-4">
            <span> {{ formattedToken(row._id) }}</span>
            <el-tooltip
              :content="i18n.t('browserExtension.copyToken')"
              placement="top">
              <Copy
                :size="18"
                class="cursor-pointer text-contrast transition-colors hover:text-blue-600"
                @click="copyToken(row._id)" />
            </el-tooltip>
          </p>
          <SmButton
            v-else
            size="smaller"
            outline
            @click="requestToken(row._id)">
            <div class="flex items-center gap-2">
              <span>{{ i18n.t('browserExtension.requestToken') }}</span>
            </div>
          </SmButton>
        </div>
      </template>

      <!-- Action -->
      <template #action="{ row }">
        <SmDropdown>
          <SmDropdownItem :icon="PencilLine" @click="editDeployment(row)">
            {{ i18n.t('edit') }}
          </SmDropdownItem>

          <SmDropdownItem :icon="Trash" @click="deleteDeployment(row)">
            {{ i18n.t('delete') }}
          </SmDropdownItem>
          <SmDropdownItem
            :icon="KeyRound"
            class="stroke-contrast"
            @click="copyToken(row._id)">
            {{ i18n.t('browserExtension.copyToken') }}
          </SmDropdownItem>
        </SmDropdown>
      </template>
    </SmTable>
  </SmDialog>

  <!-- Edit / Create Deployment Dialog -->
  <SmDialog
    v-model:visibility="editDialogVisible"
    :title="
      editMode
        ? i18n.t('editDeployment')
        : i18n.t('browserExtension.createDeployment')
    "
    size="medium">
    <SmDialogMessage
      :message="saveResponseMsg"
      :visible="showSaveResponse"
      :type="saveResponseType"
      class="mb-4" />

    <el-form
      v-model="form.fields"
      :model="form.fields"
      label-width="150px"
      label-position="top"
      require-asterisk-position="right">
      <el-form-item
        :label="i18n.t('name')"
        prop="name"
        required
        :error="form.getErrorMessage('name')">
        <SmInput
          v-model="form.fields.name"
          size="large"
          design="form"
          outline />
      </el-form-item>
      <el-form-item
        :label="i18n.t('description')"
        prop="description"
        :error="form.getErrorMessage('description')">
        <SmInput
          v-model="form.fields.description"
          size="large"
          design="form"
          outline />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="buttons">
        <SmButton
          v-if="!actionsDone"
          outline
          @click="editDialogVisible = false">
          {{ i18n.t('cancel') }}
        </SmButton>
        <SmButton v-if="!actionsDone" @click="handleSave">
          {{ editMode ? i18n.t('save') : i18n.t('create') }}
        </SmButton>
        <SmButton v-if="actionsDone" outline @click="editDialogVisible = false">
          {{ i18n.t('close') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>

  <!-- Delete Deployment Dialog -->
  <DeleteConfirmDialog
    v-model:visible="deleteConfirmDialogVisible"
    :title="i18n.t('browserExtension.deleteDeployment')"
    :sub-title="
      i18n.t('browserExtension.deleteDeploymentText', {
        name: deploymentToDelete?.name,
      })
    "
    @cancel="deleteConfirmDialogVisible = false"
    @delete="handleDelete" />
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { onUnmounted } from 'vue'
  import {
    ApiError,
    BrowserDeployment,
    BrowserDeploymentTokenOut,
    BrowserExtensionManagementService,
  } from '@/client'
  import SmTable from '@/components/sm/SmTable.vue'
  import { Column } from '@/components/sm/SmTable.types'
  import { isRequired } from '@/components/sm/SmInput/SmInputValidator'
  import {
    useBrowserDeployments,
    deleteDeployment as _deleteDeploymentInApi,
    updateDeployment as _updateDeploymentInApi,
    createDeployment as _createDeploymentInApi,
  } from '@/stores/browserextension/browserextension.store'
  import { sendToast } from '@/components/sm/SmNotification'
  import { KeyRound, PencilLine, Trash, Copy } from 'lucide-vue-next'
  import { DialogMessageTypes } from '@/components/sm/SmDialogMessage.vue'
  import useForm from '@/composables/useForm'

  const i18n = useI18n()

  const visibility = defineModel('visibility', {
    type: Boolean,
    default: false,
  })

  const props = defineProps({
    showCreateDialog: {
      type: Boolean,
      default: false,
    },
  })

  watch(
    () => props.showCreateDialog,
    (newValue) => {
      if (newValue) {
        setTimeout(() => {
          createDeployment()
        }, 0)
      }
    },
    { immediate: true }
  )

  const { data: deployments, unsubscribe: unsubDeployments } = useBrowserDeployments()

  // Cleanup subscriptions on unmount to prevent memory leaks
  onUnmounted(() => {
    unsubDeployments()
  })

  // ----------------------------------------------------------------------
  // Table
  // ----------------------------------------------------------------------

  const columns: Column<BrowserDeployment>[] = [
    {
      label: i18n.t('name'),
      key: 'name',
      width: 1,
      sortable: false,
    },
    {
      label: i18n.t('browserExtension.deploymentToken'),
      key: 'token',
      width: 1,
      sortable: false,
    },
    {
      label: '',
      key: 'action',
      width: '40px',
      sortable: false,
    },
  ]

  // ----------------------------------------------------------------------
  // Deployment Token
  // ----------------------------------------------------------------------

  const token = ref<Record<string, BrowserDeploymentTokenOut>>({})

  function formattedToken(deploymentId: string) {
    if (token.value[deploymentId]) {
      const last8 = token.value[deploymentId].deployment_token.slice(-8)
      return 'XXXX XXXX ' + last8.slice(4)
    }
    return false
  }

  async function requestToken(id: string) {
    await BrowserExtensionManagementService.createBrowserDeploymentTokenApiV1BrowserDeploymentsBrowserDeploymentIdTokenPost(
      { browserDeploymentId: id }
    )
      .then((response: BrowserDeploymentTokenOut) => {
        token.value[id] = response
      })
      .catch((error: ApiError) => {
        console.error(`Failed to load token for deployment ${id}:`, error)
      })
  }

  function copyToken(id: string) {
    if (token.value[id]?.deployment_token) {
      navigator.clipboard

        .writeText(token.value[id].deployment_token)
        .then(() => {
          sendToast(
            '',
            i18n.t('browserExtension.copyToClipboardSuccess'),
            'success'
          )
        })
        .catch((err) => {
          console.error('Failed to copy:', err)
          sendToast(
            '',
            i18n.t('browserExtension.copyToClipboardFailed'),
            'error'
          )
        })
    } else {
      sendToast('', i18n.t('browserExtension.copyToClipboardFailed'), 'error')
    }
  }

  // ----------------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------------

  // Save response dialog
  const saveResponseMsg = ref('')
  const showSaveResponse = ref(false)
  const saveResponseType = ref<DialogMessageTypes>('success')
  const deploymentToken = ref<string | undefined>('')
  const actionsDone = ref(false)

  //   Delete
  const deleteConfirmDialogVisible = ref(false)
  const deploymentToDelete = ref<BrowserDeployment>()

  function deleteDeployment(deployment: BrowserDeployment) {
    deploymentToDelete.value = deployment
    deleteConfirmDialogVisible.value = true
  }

  function handleDelete() {
    if (deploymentToDelete.value && deploymentToDelete.value._id) {
      _deleteDeploymentInApi(deploymentToDelete.value._id).then(() => {
        deleteConfirmDialogVisible.value = false
      })
    }
  }

  //   Edit
  const editDialogVisible = ref(false)
  const deploymentToEdit = ref<BrowserDeployment | null>()
  const editMode = ref(false)

  function editDeployment(deployment: BrowserDeployment) {
    form.fields.name = deployment.name
    form.fields.description = deployment.description

    deploymentToEdit.value = deployment
    editDialogVisible.value = true
    editMode.value = true
  }

  //   Create
  function createDeployment() {
    deploymentToEdit.value = null
    editDialogVisible.value = true
    editMode.value = false
  }

  function handleSave() {
    const validation = form.validate()
    if (!validation) {
      saveResponseMsg.value = i18n.t('general.form.fillInAllFields.message')
      saveResponseType.value = 'error'
      showSaveResponse.value = true
      return
    }

    if (editMode.value && deploymentToEdit.value) {
      _updateDeploymentInApi(deploymentToEdit.value._id, form.fields)
        .then(() => {
          saveResponseMsg.value = i18n.t('browserExtension.deploymentSaved')
          saveResponseType.value = 'success'
          showSaveResponse.value = true
          actionsDone.value = true
        })
        .catch((error) => {
          saveResponseMsg.value = error.message
          saveResponseType.value = 'error'
          showSaveResponse.value = true
        })
    } else {
      _createDeploymentInApi(form.fields)
        .then(() => {
          saveResponseType.value = 'success'
          saveResponseMsg.value = i18n.t('browserExtension.deploymentCreated')
          showSaveResponse.value = true
          actionsDone.value = true
        })
        .catch((error) => {
          saveResponseMsg.value = error.message
          saveResponseType.value = 'error'
          showSaveResponse.value = true
        })
    }
  }

  watch(editDialogVisible, (newValue) => {
    if (!newValue) {
      form.reset()
      form.clearError()
      actionsDone.value = false
      saveResponseMsg.value = ''
      showSaveResponse.value = false
      saveResponseType.value = 'success'
      deploymentToken.value = ''
    }
  })

  // ----------------------------------------------------------------------
  // Form
  // ----------------------------------------------------------------------

  const form = useForm(
    {
      name: deploymentToEdit.value?.name || '',
      description: '',
    },
    // Form Hooks
    {
      onReset: () => {},
    },
    // Form Validators
    {
      name: [isRequired],
    },
    {}
  )
</script>
